import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Pagination,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  KeyboardArrowRight as ArrowIcon,
  DiamondRounded as DiamondIcon,
} from "@mui/icons-material";
import axios from "axios";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { apiErrorMessage } from "../../utils/apiError";
import { reportSwitcherStripSx } from "./reportLayout";

/* ─── CONSTANTS ──────────────────────────────────────────────── */
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
const FLOOR_OPTIONS = [1, 2, 3, 4, 5];
const BARCODE_OPTIONS = [1, 2, 3, 4];
const ITEMS_PER_PAGE = 7;

const emptyForm = {
  id: null,
  name: "",
  code: "",
  description: "",
  defaultPurity: "",
  defaultPurityOldMetal: "",
  defaultIssuePurity: "",
  defaultMargin: "",
  defaultFloor: 1,
  defaultBarcode: 1,
  isActive: true,
  prefixConfigData: null,
};

/* ─── Blue/White Design Tokens (shared with Pawn module) ─────── */
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const BG_ALT = "#F1F5F9";
const SUCCESS = "#059669";

/* ─── SHARED FIELD STYLES (pawn) ─────────────────────────────── */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: SURFACE,
    borderRadius: 2,
    fontSize: "0.85rem",
    color: INK,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: BLUE },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
  "& input::placeholder": { color: INK_MUTED, opacity: 1 },
  "& textarea::placeholder": { color: INK_MUTED, opacity: 1 },
};

const selectSx = {
  bgcolor: SURFACE,
  borderRadius: 2,
  fontSize: "0.85rem",
  color: INK,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: BLUE,
    borderWidth: "1.5px",
  },
  "& .MuiSvgIcon-root": { color: INK_MUTED },
};

const menuPropsSx = {
  PaperProps: {
    sx: {
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      boxShadow: "0 8px 30px rgba(15,23,42,0.12)",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        fontSize: "0.83rem",
        color: INK_SOFT,
        py: 1,
        "&:hover": { bgcolor: BLUE_PALE, color: BLUE_DARK },
        "&.Mui-selected": {
          bgcolor: BLUE_PALE,
          color: BLUE_DARK,
          fontWeight: 700,
        },
      },
    },
  },
};

// Switch styled BLUE when checked
const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: BLUE },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: BLUE,
  },
};

/* ─── MICRO COMPONENTS ──────────────────────────────────────── */
const Label = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: "0.7rem",
      fontWeight: 600,
      color: INK_MUTED,
      mb: 0.6,
      letterSpacing: "0.01em",
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: BLUE, ml: 0.3 }}>
        *
      </Box>
    )}
  </Typography>
);

const SectionHead = ({ label }) => (
  <Typography
    sx={{
      fontSize: "0.7rem",
      fontWeight: 700,
      color: BLUE_DARK,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      mb: 1.5,
    }}
  >
    {label}
  </Typography>
);

const StatusChip = ({ status }) => (
  <Chip
    label={status ? "Active" : "Inactive"}
    size="small"
    sx={{
      height: 22,
      fontSize: "0.68rem",
      fontWeight: 700,
      bgcolor: status ? SUCCESS : BG_ALT,
      color: status ? "#fff" : INK_MUTED,
    }}
  />
);

// Send null instead of ""
const toNull = (value) =>
  value === "" || value === undefined || value === null ? null : value;

// def_purity and friends are decimal(5,2) and arrive as "91.60" or 91.6 —
// normalise so the form shows one shape whatever the API sent.
const asPct = (value) =>
  value === "" || value === undefined || value === null || isNaN(Number(value))
    ? ""
    : Number(value).toFixed(2);

// A purity percentage, typed rather than picked. These used to be dropdowns fed
// by Purity Master; purities are no longer records of their own — a rate belongs
// to the metal and an ornament carries its own percentage — so the figure is
// entered directly. 0–100 only: anything else is a slip, not a purity.
const PurityPercentField = ({ value, onChange, placeholder = "e.g. 91.60" }) => (
  <TextField
    value={value}
    onChange={(e) => {
      const next = e.target.value;
      if (next === "") return onChange("");
      const num = parseFloat(next);
      if (isNaN(num) || num < 0 || num > 100) return;
      onChange(next);
    }}
    onBlur={(e) => onChange(asPct(e.target.value))}
    type="number"
    size="small"
    fullWidth
    placeholder={placeholder}
    inputProps={{ min: 0, max: 100, step: "0.01" }}
    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
    sx={fieldSx}
  />
);

/* ─── HEAD / BODY CELL STYLES (match reports) ────────────────── */
const headCellSx = {
  bgcolor: "#F8FAFC",
  color: INK_MUTED,
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  borderBottom: `1px solid ${BORDER}`,
  whiteSpace: "nowrap",
};
const bodyCellSx = {
  fontSize: "0.8rem",
  color: INK_SOFT,
  borderBottom: `1px solid ${BORDER}`,
  py: 1,
};

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
 * Two shapes, same logic and same CRUD:
 *   • standalone / embedded → the full-width table screen. This is what the
 *     Masters screen mounts.
 *   • panel → the narrow metal LIST, where a row CLICK selects the metal and
 *     only the pencil opens the edit dialog. Built for the old "Metal & Purity"
 *     master, which went when purities stopped being records of their own; kept
 *     for the next master that wants a metal list beside it. NO CALLER TODAY.
 * ─────────────────────────────────────────────────────────────────────────── */
export default function PawnMetalTypeMaster({
  embedded = false,
  panel = false,
  selectedMetalId: controlledMetalId,
  onSelectMetal,
  onMetalsLoaded,
  refreshToken = 0,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { currentStoreInfo, userInfo } = useContext(AutoTaskAuthContext) || {};
  const loggedUser =
    JSON.parse(localStorage.getItem("CapacitorStorage.user") || "null") || {};
  const storeFromLS =
    JSON.parse(
      localStorage.getItem("CapacitorStorage.current_store_info") || "null",
    ) || {};
  const store_id =
    currentStoreInfo?.id ??
    storeFromLS?.id ??
    userInfo?.store_id ??
    loggedUser?.store_id;
  const user_id = userInfo?.id ?? loggedUser?.id;

  const [activeTab, setActiveTab] = useState("all");
  const [metals, setMetals] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState(emptyForm);
  const [selectedId, setSelectedId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [savingMetal, setSavingMetal] = useState(false);
  const [savingPrefixes, setSavingPrefixes] = useState(false);
  const [metalCounts, setMetalsCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Financial Year state
  const [financialYears, setFinancialYears] = useState([]);
  const [loadingFY, setLoadingFY] = useState(false);

  // Prefix table state
  const [prefixTableData, setPrefixTableData] = useState([]);
  const [showPrefixSection, setShowPrefixSection] = useState(false);


  useEffect(() => {
    if (store_id !== null && store_id !== undefined) {
      fetchMetals();
      fetchFinancialYears();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store_id, activeTab, refreshToken]);

  // Fetch financial years
  const fetchFinancialYears = async () => {
    setLoadingFY(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/accounts/financial-year`);
      if (res.data?.success) {
        setFinancialYears(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch financial years:", err);
    } finally {
      setLoadingFY(false);
    }
  };

  // Fetch all metals for the store
  const fetchMetals = useCallback(async () => {
    setLoadingList(true);
    try {
      const url = `${BASE_URL}/api/jewel-master/metal-type-master/store/${store_id}`;

      let params = {};

      if (activeTab === "active") {
        params.is_active = true;
      } else if (activeTab === "inactive") {
        params.is_active = false;
      }

      const res = await axios.get(url, { params });

      if (res.data?.success) {
        const list = res.data.data || [];
        setMetals(list);
        setMetalsCounts({
          total: res.data.total,
          active: res.data.active,
          inactive: res.data.inactive,
        });
        // Lets the merged screen keep its selected metal in step with the list
        // (re-picking the same row's fresh record after an edit).
        onMetalsLoaded?.(list);
      }
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "Failed to fetch metal types"), "error");
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store_id, activeTab]);

  // Fetch single metal by id and populate form
  const fetchMetalById = async (metalId) => {
    setLoadingForm(true);
    try {
      const url = `${BASE_URL}/api/jewel-master/metal-type-master/${metalId}`;
      const res = await axios.get(url);
      if (res.data?.success) {
        const d = res.data.data;
        setFormData({
          id: d.id,
          code: d.code || "",
          name: d.name || "",
          description: d.description || "",
          defaultPurity: asPct(d.def_purity),
          defaultPurityOldMetal: asPct(d.def_oldmetal),
          defaultIssuePurity: asPct(d.def_old_issue_metal),
          defaultMargin: d.defmargin || "",
          defaultFloor: d.floor_dflt || 1,
          defaultBarcode: d.default_barcode || 1,
          isActive: d.is_active ?? true,
          prefixConfigData: d.prefixes || null,
        });

        // Initialize prefix table data
        if (d.prefixes && Array.isArray(d.prefixes)) {
          const tableData = d.prefixes.map((prefix) => ({
            prefix_master_id: prefix.id,
            prefix_name: prefix.name,
            prefix_description: prefix.description,
            prefix_value: prefix.prefix_config?.prefix_value || "",
            separator: prefix.prefix_config?.separator || "-",
            serial_length: prefix.prefix_config?.serial_length || 5,
            fy_format: prefix.prefix_config?.fy_format || "YY-YY",
            current_fy: prefix.prefix_config?.current_fy || "",
            include_fy: prefix.prefix_config?.include_fy ?? false,
            reset_on_fy: prefix.prefix_config?.reset_on_fy ?? false,
            is_active: prefix.prefix_config?.is_active ?? false,
            config_id: prefix.prefix_config?.id || null,
          }));
          setPrefixTableData(tableData);
          setShowPrefixSection(true);
        }

        // Purity dropdowns are fed by this metal's own purities
      }
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "Failed to fetch metal details"), "error");
    } finally {
      setLoadingForm(false);
    }
  };

  // Create new metal type
  const createMetal = async () => {
    const payload = {
      store_id,
      code: formData.code,
      name: formData.name,
      description: formData.description,
      def_purity: toNull(formData.defaultPurity),
      defmargin: toNull(formData.defaultMargin),
      def_old_issue_metal: toNull(formData.defaultIssuePurity),
      def_oldmetal: toNull(formData.defaultPurityOldMetal),
      is_active: formData.isActive,
      floor_dflt: formData.defaultFloor,
      default_barcode: formData.defaultBarcode,
      created_by: user_id,
    };
    const url = `${BASE_URL}/api/jewel-master/metal-type-master`;
    const res = await axios.post(url, payload);
    if (!res.data?.success)
      throw new Error(res.data?.message || "Create failed");
    return res.data.data;
  };

  // Update existing metal type
  const updateMetal = async () => {
    const payload = {
      store_id,
      code: formData.code,
      name: formData.name,
      description: formData.description,
      def_purity: toNull(formData.defaultPurity),
      defmargin: toNull(formData.defaultMargin),
      def_old_issue_metal: toNull(formData.defaultIssuePurity),
      def_oldmetal: toNull(formData.defaultPurityOldMetal),
      is_active: formData.isActive,
      floor_dflt: formData.defaultFloor,
      default_barcode: formData.defaultBarcode,
      updated_by: user_id,
    };
    const url = `${BASE_URL}/api/jewel-master/metal-type-master/${formData.id}`;
    const res = await axios.put(url, payload);
    if (!res.data?.success)
      throw new Error(res.data?.message || "Update failed");
    return res.data.data;
  };

  // Handle METAL DETAILS save
  const handleSaveMetal = async () => {
    if (!formData.name.trim()) {
      showSnackbar("Metal name is required", "error");
      return;
    }
    setSavingMetal(true);
    try {
      let metalData;
      if (editMode) {
        metalData = await updateMetal();
        showSnackbar("Metal updated successfully", "success");
      } else {
        metalData = await createMetal();
        showSnackbar("Metal created successfully", "success");
        // Update form with the new ID
        setFormData((prev) => ({ ...prev, id: metalData.id }));
        setEditMode(true);
        setSelectedId(metalData.id);
      }

      // Show prefix section after metal is saved
      if (!showPrefixSection) {
        // Fetch metal again to get prefix structure
        await fetchMetalById(metalData.id);
      }

      await fetchMetals();
    } catch (err) {
      showSnackbar(
        apiErrorMessage(
          err,
          editMode ? "Failed to update metal type" : "Failed to create metal type",
        ),
        "error",
      );
    } finally {
      setSavingMetal(false);
    }
  };

  // Handle BULK PREFIX save
  const handleSavePrefixes = async () => {
    if (!formData.id) {
      showSnackbar("Please save metal details first", "error");
      return;
    }

    // Filter only prefixes with values
    const prefixesToSave = prefixTableData.filter((p) => p.prefix_value.trim());

    if (prefixesToSave.length === 0) {
      showSnackbar("Please enter at least one prefix value", "error");
      return;
    }

    // Validate all prefixes with values have current_fy
    const missingFY = prefixesToSave.filter((p) => !p.current_fy);
    if (missingFY.length > 0) {
      showSnackbar(
        "All prefixes with values must have a Financial Year selected",
        "error",
      );
      return;
    }

    setSavingPrefixes(true);
    try {
      const payload = {
        store_id,
        metal_type_id: formData.id,
        prefixes: prefixesToSave.map((p) => ({
          prefix_master_id: p.prefix_master_id,
          prefix_value: p.prefix_value,
          separator: p.separator,
          serial_length: p.serial_length,
          include_fy: p.include_fy,
          fy_format: p.fy_format,
          current_fy: p.current_fy,
          reset_on_fy: p.reset_on_fy,
          is_active: p.is_active,
        })),
        updated_by: user_id,
      };

      const url = `${BASE_URL}/api/jewel-master/metal-prefix-config/bulk-upsert`;
      const res = await axios.post(url, payload);

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to save prefixes");
      }

      showSnackbar(
        `Prefixes saved: ${res.data.summary.created} created, ${res.data.summary.updated} updated`,
        "success",
      );

      // Refresh metal data to show updated configs
      await fetchMetalById(formData.id);
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "Failed to save prefixes"), "error");
    } finally {
      setSavingPrefixes(false);
    }
  };

  // Open add form with blank state
  const handleOpenAdd = () => {
    setEditMode(false);
    setFormData(emptyForm);
    setSelectedId(null);
    setPrefixTableData([]);
    setShowPrefixSection(false);
    setIsFormOpen(true);
  };

  // Open edit form and fetch metal data by id
  const handleOpenEdit = async (metal) => {
    setEditMode(true);
    setSelectedId(metal.id);
    setIsFormOpen(true);
    await fetchMetalById(metal.id);
  };

  // Row click: in panel mode a click SELECTS the metal (the purity panel beside
  // it follows); everywhere else it still opens the edit dialog.
  const handleRowClick = (metal) => {
    if (panel && onSelectMetal) onSelectMetal(metal);
    else handleOpenEdit(metal);
  };

  // Close form panel and reset state
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedId(null);
    setFormData(emptyForm);
    setPrefixTableData([]);
    setShowPrefixSection(false);
  };

  // Update a single field in form state
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Update prefix table row
  const handlePrefixChange = (index, field, value) => {
    setPrefixTableData((prev) => {
      const newData = [...prev];
      newData[index] = { ...newData[index], [field]: value };
      return newData;
    });
  };

  // Update search and reset to page 1
  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const filtered = metals.filter((m) => {
    const q = search.toLowerCase();
    return (
      !q ||
      m.name?.toLowerCase().includes(q) ||
      m.code?.toLowerCase().includes(q) ||
      m.description?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  // Panel mode is driven from outside; standalone highlights the row whose
  // dialog is open.
  const highlightedId = panel ? controlledMetalId : selectedId;

  const outerSx = panel
    ? {
        p: 0,
        width: "100%",
        minWidth: 0,
        // From md up it fills the column it was handed and the list scrolls
        // inside it. Stacked on a phone it sizes to its content instead — two
        // panels splitting one screen leaves neither enough rows to be useful,
        // so there the page scrolls.
        flex: { xs: "0 0 auto", lg: 1 },
        minHeight: { xs: "auto", lg: 0 },
        display: "flex",
        flexDirection: "column",
      }
    : embedded
    ? { p: 0, minWidth: 0 }
    : {
        height: "100%",
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: BG_ALT,
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
      };

  return (
    <Box sx={outerSx}>
      {/* ── Header (hidden when embedded in the unified Masters screen) ── */}
      {!embedded && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            <DiamondIcon />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK }}
            >
              Metal Type Master
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
              {currentStoreInfo?.name
                ? `${currentStoreInfo.name} · Inventory Control`
                : "Inventory Control"}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── PANEL MODE: the metal list beside the purity panel ── */}
      {panel && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: 3,
            bgcolor: SURFACE,
            width: "100%",
            flex: { xs: "0 0 auto", lg: 1 },
            minHeight: { xs: "auto", lg: 0 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Card head — title, count and Add */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
            >
              <Typography
                noWrap
                sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}
              >
                Metal Types
              </Typography>
              <Chip
                label={filtered.length}
                size="small"
                sx={{
                  height: 20,
                  bgcolor: BLUE_PALE,
                  color: BLUE_DARK,
                  fontWeight: 700,
                  fontSize: "0.7rem",
                }}
              />
            </Box>
            <Button
              onClick={handleOpenAdd}
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{
                bgcolor: BLUE,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                whiteSpace: "nowrap",
                px: 1.25,
                "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
              }}
            >
              Metal
            </Button>
          </Box>

          {/* Search + status filter */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              bgcolor: "#F8FAFC",
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <TextField
              placeholder="Search metals…"
              size="small"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: INK_MUTED, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={reportSwitcherStripSx}>
              <ToggleButtonGroup
                value={activeTab}
                exclusive
                size="small"
                onChange={(e, v) => {
                  if (v) {
                    setActiveTab(v);
                    setPage(1);
                  }
                }}
                sx={{
                  width: "auto",
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    px: 1.25,
                    py: 0.4,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: INK_MUTED,
                    borderColor: BORDER,
                    bgcolor: SURFACE,
                  },
                  "& .Mui-selected": {
                    bgcolor: `${BLUE} !important`,
                    color: "#fff !important",
                  },
                }}
              >
                <ToggleButton value="all">All {metalCounts.total}</ToggleButton>
                <ToggleButton value="active">
                  Active {metalCounts.active}
                </ToggleButton>
                <ToggleButton value="inactive">
                  Inactive {metalCounts.inactive}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* The list itself — one row per metal, click to select */}
          {loadingList ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: BLUE }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
              <DiamondIcon sx={{ fontSize: 40, color: BORDER, mb: 1 }} />
              <Typography sx={{ color: INK_MUTED, fontSize: "0.82rem" }}>
                No metal types found. Use the "+ Metal" button to add one.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: { xs: "0 0 auto", lg: 1 },
                minHeight: 0,
                // Stacked on a phone the list caps itself instead of running the
                // purity panel off the bottom of the page.
                maxHeight: { xs: 320, lg: "none" },
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {paginated.map((m) => {
                const isSelected = highlightedId === m.id;
                return (
                  <Box
                    key={m.id}
                    onClick={() => handleRowClick(m)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 2,
                      py: 1.1,
                      cursor: "pointer",
                      borderBottom: `1px solid ${BORDER}`,
                      // A left rail rather than a full tint keeps the selected
                      // row obvious without fighting the hover state.
                      borderLeft: `3px solid ${
                        isSelected ? BLUE : "transparent"
                      }`,
                      bgcolor: isSelected ? BLUE_PALE : "transparent",
                      "&:hover": { bgcolor: BLUE_PALE },
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isSelected ? BLUE : BLUE_PALE,
                        color: isSelected ? "#fff" : BLUE_DARK,
                      }}
                    >
                      <DiamondIcon sx={{ fontSize: 18 }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            color: INK,
                          }}
                        >
                          {m.name}
                        </Typography>
                        {m.code && (
                          <Chip
                            label={m.code}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              fontFamily: "monospace",
                              bgcolor: BLUE_PALE,
                              color: BLUE_DARK,
                            }}
                          />
                        )}
                        <Tooltip title={m.is_active ? "Active" : "Inactive"}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              flexShrink: 0,
                              bgcolor: m.is_active ? SUCCESS : "#CBD5E1",
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography
                        noWrap
                        sx={{ fontSize: "0.68rem", color: INK_MUTED }}
                      >
                        Purity {m.def_purity ?? "—"} · Margin{" "}
                        {m.defmargin != null ? `${m.defmargin}%` : "—"}
                      </Typography>
                    </Box>

                    <Tooltip title="Edit metal" placement="left">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEdit(m);
                        }}
                        sx={{ color: BLUE, flexShrink: 0 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          )}

          {totalPages > 1 && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                display: "flex",
                justifyContent: "center",
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                siblingCount={0}
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: 2,
                    fontSize: "0.72rem",
                    color: INK_SOFT,
                    "&:hover": { bgcolor: BLUE_PALE, color: BLUE_DARK },
                  },
                  "& .Mui-selected": {
                    bgcolor: `${BLUE} !important`,
                    color: "#fff !important",
                  },
                }}
              />
            </Box>
          )}
        </Card>
      )}

      {/* ── Controls Card (filters + search + add) ── */}
      {!panel && (
        <>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          mb: 2,
          bgcolor: SURFACE,
        }}
      >
        <CardContent
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            "&:last-child": { pb: { xs: 1.5, md: 2 } },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <ToggleButtonGroup
              value={activeTab}
              exclusive
              size="small"
              onChange={(e, v) => {
                if (v) {
                  setActiveTab(v);
                  setPage(1);
                }
              }}
              sx={{
                flexWrap: "wrap",
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 1.5,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: INK_SOFT,
                  borderColor: BORDER,
                },
                "& .Mui-selected": {
                  bgcolor: `${BLUE} !important`,
                  color: "#fff !important",
                },
              }}
            >
              <ToggleButton value="all">All ({metalCounts.total})</ToggleButton>
              <ToggleButton value="active">
                Active ({metalCounts.active})
              </ToggleButton>
              <ToggleButton value="inactive">
                Inactive ({metalCounts.inactive})
              </ToggleButton>
            </ToggleButtonGroup>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                placeholder="Search name, code or description…"
                size="small"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                sx={{ width: { xs: "100%", sm: 280 }, ...fieldSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: INK_MUTED, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                onClick={handleOpenAdd}
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: BLUE,
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
                }}
              >
                Add Metal
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* ── Table Card ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          bgcolor: SURFACE,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}>
            Metal Types
          </Typography>
          <Chip
            label={`${filtered.length} ${
              filtered.length === 1 ? "result" : "results"
            }`}
            size="small"
            sx={{
              bgcolor: BLUE_PALE,
              color: BLUE_DARK,
              fontWeight: 700,
              fontSize: "0.72rem",
            }}
          />
        </Box>

        {loadingList ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: BLUE }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <DiamondIcon sx={{ fontSize: 44, color: BORDER, mb: 1 }} />
            <Typography sx={{ color: INK_MUTED, fontSize: "0.9rem" }}>
              No metal types found. Click "Add Metal" to create one.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                maxHeight: {
                  xs: "calc(100vh - 360px)",
                  md: "calc(100vh - 320px)",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Metal Name</TableCell>
                    <TableCell sx={headCellSx}>Code</TableCell>
                    <TableCell sx={headCellSx}>Description</TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Purity
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Old Metal
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Margin %
                    </TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    <TableCell align="center" sx={headCellSx}>
                      Edit
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((m) => (
                    <TableRow
                      key={m.id}
                      hover
                      onClick={() => handleOpenEdit(m)}
                      sx={{
                        cursor: "pointer",
                        bgcolor:
                          selectedId === m.id ? BLUE_PALE : "transparent",
                        "&:hover": { bgcolor: BLUE_PALE },
                      }}
                    >
                      <TableCell
                        sx={{ ...bodyCellSx, fontWeight: 700, color: INK }}
                      >
                        {m.name}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          label={m.code}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: BLUE_PALE,
                            color: BLUE_DARK,
                            fontFamily: "monospace",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: INK_MUTED }}>
                        {m.description || "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...bodyCellSx,
                          fontWeight: 700,
                          fontFamily: "monospace",
                          color: INK,
                        }}
                      >
                        {m.def_purity ?? "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...bodyCellSx, fontFamily: "monospace" }}
                      >
                        {m.def_oldmetal ?? "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...bodyCellSx, fontWeight: 600, color: BLUE }}
                      >
                        {m.defmargin != null ? `${m.defmargin}%` : "—"}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <StatusChip status={m.is_active} />
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        <Tooltip title="Edit" placement="left">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEdit(m);
                            }}
                            sx={{ color: BLUE }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "flex-end",
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: 2,
                    fontSize: "0.75rem",
                    color: INK_SOFT,
                    "&:hover": { bgcolor: BLUE_PALE, color: BLUE_DARK },
                  },
                  "& .Mui-selected": {
                    bgcolor: `${BLUE} !important`,
                    color: "#fff !important",
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>
        </>
      )}

      {/* ── FORM DIALOG ── */}
      <Dialog
        open={isFormOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
      >
        <DialogTitle
          sx={{
            bgcolor: BLUE,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DiamondIcon />
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.1 }}
              >
                {editMode
                  ? formData.name || "Edit Metal"
                  : "Add Metal Type"}
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>
                {editMode ? "Editing Configuration" : "New Metal Asset"}
                {editMode && formData.id ? ` · ID #${formData.id}` : ""}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseForm}
            sx={{ color: "#fff" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "#fff" }}>
          {loadingForm ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
                gap: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: BLUE }} />
              <Typography sx={{ fontSize: "0.9rem", color: INK_MUTED }}>
                Loading…
              </Typography>
            </Box>
          ) : (
            <>
              {/* SECTION 1: METAL DETAILS */}
              <Box sx={{ mb: 3 }}>
                <SectionHead label="Metal Details" />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Label required>Metal Name</Label>
                    <TextField
                      value={formData.name}
                      onChange={(e) =>
                        handleFormChange("name", e.target.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g. 24K Gold Bullion"
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Label>Code</Label>
                    <TextField
                      value={formData.code}
                      onChange={(e) =>
                        handleFormChange("code", e.target.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g. GB"
                      sx={{
                        ...fieldSx,
                        "& input": {
                          fontFamily: "monospace",
                          letterSpacing: "0.04em",
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Label>Description</Label>
                    <TextField
                      value={formData.description}
                      onChange={(e) =>
                        handleFormChange("description", e.target.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="Brief description of this metal asset…"
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Label>Default Purity %</Label>
                    <PurityPercentField
                      value={formData.defaultPurity}
                      onChange={(v) => handleFormChange("defaultPurity", v)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Label>Default Purity Old %</Label>
                    <PurityPercentField
                      value={formData.defaultPurityOldMetal}
                      onChange={(v) =>
                        handleFormChange("defaultPurityOldMetal", v)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Label>Default Issue Purity %</Label>
                    <PurityPercentField
                      value={formData.defaultIssuePurity}
                      onChange={(v) =>
                        handleFormChange("defaultIssuePurity", v)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      sx={{ fontSize: "0.7rem", color: INK_MUTED, mt: -1 }}
                    >
                      Default Purity is what the Payment screen opens the
                      ornament row on — 91.6 for hallmarked 22 KT gold. The
                      teller can still type a different figure per piece.
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Label>Default Floor</Label>
                    <Select
                      value={formData.defaultFloor}
                      onChange={(e) =>
                        handleFormChange("defaultFloor", e.target.value)
                      }
                      size="small"
                      fullWidth
                      sx={selectSx}
                      MenuProps={menuPropsSx}
                    >
                      {FLOOR_OPTIONS.map((v) => (
                        <MenuItem key={v} value={v}>
                          Floor {v}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Label>Default Barcode</Label>
                    <Select
                      value={formData.defaultBarcode}
                      onChange={(e) =>
                        handleFormChange("defaultBarcode", e.target.value)
                      }
                      size="small"
                      fullWidth
                      sx={selectSx}
                      MenuProps={menuPropsSx}
                    >
                      {BARCODE_OPTIONS.map((v) => (
                        <MenuItem key={v} value={v}>
                          Barcode {v}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Label>Default Margin (%)</Label>
                    <TextField
                      value={formData.defaultMargin}
                      onChange={(e) =>
                        handleFormChange("defaultMargin", e.target.value)
                      }
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="e.g. 2.5"
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) =>
                            handleFormChange("isActive", e.target.checked)
                          }
                          sx={switchSx}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: INK_SOFT,
                          }}
                        >
                          Active
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                {/* Metal Details Save Button */}
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${BORDER}`,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    onClick={handleSaveMetal}
                    disabled={savingMetal}
                    variant="contained"
                    endIcon={
                      savingMetal ? (
                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    sx={{
                      bgcolor: BLUE,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "none",
                      "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
                      "&.Mui-disabled": { opacity: 0.6 },
                    }}
                  >
                    {savingMetal
                      ? "Saving…"
                      : editMode
                      ? "Update Metal Details"
                      : "Save Metal Details"}
                  </Button>
                </Box>
              </Box>

              {/* SECTION 2: PREFIX CONFIGURATION */}
              {(showPrefixSection || editMode) && formData.id && (
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 3,
                    border: `1px solid ${BORDER}`,
                    bgcolor: BLUE_PALE,
                  }}
                >
                  <SectionHead label="Prefix Configuration" />

                  <TableContainer
                    sx={{
                      border: `1px solid ${BORDER}`,
                      borderRadius: 2,
                      bgcolor: "#fff",
                      overflowX: "auto",
                    }}
                  >
                    <Table size="small" sx={{ minWidth: 720 }}>
                      <TableHead>
                        <TableRow>
                          {[
                            "Transaction Type",
                            "Prefix Value",
                            "Separator",
                            "Length",
                            "FY Format",
                            "Financial Year",
                            "Inc FY",
                            "Reset",
                            "Active",
                          ].map((h, i) => (
                            <TableCell
                              key={h}
                              align={i >= 3 && i !== 4 && i !== 5 ? "center" : "left"}
                              sx={{ ...headCellSx, py: 0.8 }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {prefixTableData.map((prefix, index) => (
                          <TableRow
                            key={prefix.prefix_master_id}
                            sx={{
                              "&:hover": { bgcolor: BLUE_PALE },
                              "& td": {
                                py: 0.8,
                                borderBottom: `1px solid ${BORDER}`,
                              },
                            }}
                          >
                            {/* Transaction Type */}
                            <TableCell sx={{ minWidth: 140 }}>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: INK,
                                  fontWeight: 600,
                                }}
                              >
                                {prefix.prefix_name}
                              </Typography>
                              <Typography
                                sx={{ fontSize: "0.62rem", color: INK_MUTED }}
                              >
                                {prefix.prefix_description}
                              </Typography>
                            </TableCell>

                            {/* Prefix Value */}
                            <TableCell>
                              <TextField
                                size="small"
                                value={prefix.prefix_value}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "prefix_value",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g. SB"
                                sx={{
                                  width: 80,
                                  ...fieldSx,
                                  "& .MuiInputBase-root": {
                                    height: 30,
                                    fontSize: "0.72rem",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                  },
                                }}
                              />
                            </TableCell>

                            {/* Separator */}
                            <TableCell>
                              <Select
                                size="small"
                                value={prefix.separator}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "separator",
                                    e.target.value,
                                  )
                                }
                                MenuProps={menuPropsSx}
                                sx={{
                                  width: 56,
                                  height: 30,
                                  fontSize: "0.72rem",
                                  ...selectSx,
                                }}
                              >
                                {["-", "/", "_", "."].map((sep) => (
                                  <MenuItem key={sep} value={sep}>
                                    {sep}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>

                            {/* Serial Length */}
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={prefix.serial_length}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "serial_length",
                                    parseInt(e.target.value) || 5,
                                  )
                                }
                                inputProps={{ min: 3, max: 10 }}
                                sx={{
                                  width: 56,
                                  ...fieldSx,
                                  "& .MuiInputBase-root": {
                                    height: 30,
                                    fontSize: "0.72rem",
                                  },
                                }}
                              />
                            </TableCell>

                            {/* FY Format */}
                            <TableCell>
                              <Select
                                size="small"
                                value={prefix.fy_format}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "fy_format",
                                    e.target.value,
                                  )
                                }
                                MenuProps={menuPropsSx}
                                sx={{
                                  width: 110,
                                  height: 30,
                                  fontSize: "0.72rem",
                                  ...selectSx,
                                }}
                              >
                                <MenuItem value="YY-YY">YY-YY</MenuItem>
                                <MenuItem value="YYYY-YYYY">YYYY-YYYY</MenuItem>
                              </Select>
                            </TableCell>

                            {/* Financial Year */}
                            <TableCell>
                              <Select
                                size="small"
                                value={prefix.current_fy}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "current_fy",
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                MenuProps={menuPropsSx}
                                sx={{
                                  width: 110,
                                  height: 30,
                                  fontSize: "0.72rem",
                                  ...selectSx,
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select FY
                                </MenuItem>
                                {financialYears.map((fy) => (
                                  <MenuItem key={fy.id} value={fy.fy_label}>
                                    {fy.fy_label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>

                            {/* Include FY */}
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={prefix.include_fy}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "include_fy",
                                    e.target.checked,
                                  )
                                }
                                sx={switchSx}
                              />
                            </TableCell>

                            {/* Reset on FY */}
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={prefix.reset_on_fy}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "reset_on_fy",
                                    e.target.checked,
                                  )
                                }
                                sx={switchSx}
                              />
                            </TableCell>

                            {/* Active */}
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={prefix.is_active}
                                onChange={(e) =>
                                  handlePrefixChange(
                                    index,
                                    "is_active",
                                    e.target.checked,
                                  )
                                }
                                sx={switchSx}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Prefix Save Button */}
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${BORDER}`,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      onClick={handleSavePrefixes}
                      disabled={savingPrefixes || !formData.id}
                      variant="contained"
                      endIcon={
                        savingPrefixes ? (
                          <CircularProgress size={14} sx={{ color: "#fff" }} />
                        ) : (
                          <ArrowIcon />
                        )
                      }
                      sx={{
                        bgcolor: BLUE,
                        textTransform: "none",
                        fontWeight: 700,
                        boxShadow: "none",
                        "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
                        "&.Mui-disabled": { opacity: 0.6 },
                      }}
                    >
                      {savingPrefixes
                        ? "Saving Prefixes…"
                        : "Save Prefix Configuration"}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: BG_ALT }}>
          <Button
            onClick={handleCloseForm}
            disabled={savingMetal || savingPrefixes}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: BLUE,
              borderColor: BLUE,
              "&:hover": { borderColor: BLUE_DARK, bgcolor: BLUE_PALE },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SNACKBAR ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
